import { zodResolver } from '@hookform/resolvers/zod';
import { UseFormReturn, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Grid } from '@mui/material';
import { splitArrayAtMiddle } from 'utils/helpers';
import { Box } from '@mui/system';
import {
  CheckboxInputField,
  ControlledField,
  FormControlGroup,
  StyledTooltip,
  SwitchInputField,
  VehicleExtrasEnum,
} from 'remote/global';
import { HelpOutlineOutlined } from '@mui/icons-material';
import { usePutOperatorsVehiclesMutation } from 'api/queries/operators';
import { VehicleExtrasSchemaType, vehicleExtrasSchema } from './types';
import { VehicleDtoSchema } from '../BookingFormFactory/types';

const extrasList: VehicleExtrasEnum[][] = splitArrayAtMiddle(Object.keys(VehicleExtrasEnum));

export function VehicleExtrasFormFields({
  form,
  focusInitial,
}: {
  form: UseFormReturn<VehicleExtrasSchemaType>;
  focusInitial?: boolean;
}) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;
  // We have to use 'useWatch' because it will get rid of initial 'register' call setting the initial field value as 'undefined'
  const values = useWatch(form);
  const [availableForOffer, kidFriendly, acceptInternationalRides] = watch([
    'availableForOffer',
    'kidFriendly',
    'acceptInternationalRides',
  ]);
  const { extras = [] } = values ?? {};

  return (
    <>
      <ControlledField
        control={control}
        name='availableForOffer'
        render={(renderProps) => (
          <SwitchInputField
            label={
              <StyledTooltip
                title={t('modals.createVehicle.steps.vehicleExtras.busActiveTooltip')}
                placement='top'
              >
                <div>
                  {t('FormFields.availableForOffer.label')}
                  <HelpOutlineOutlined
                    color='primary'
                    fontSize='small'
                    sx={{ verticalAlign: 'middle', ml: 0.5 }}
                  />
                </div>
              </StyledTooltip>
            }
            labelProps={{
              labelPlacement: 'start',
              sx: { mb: 2 },
            }}
            focusInitial={focusInitial}
            {...renderProps}
            field={{
              ...renderProps.field,
              value: availableForOffer,
            }}
          />
        )}
      />

      <FormControlGroup error={errors?.acceptInternationalRides}>
        <Grid container>
          <ControlledField
            control={control}
            name='acceptInternationalRides'
            render={(renderProps) => (
              <SwitchInputField
                label={
                  <StyledTooltip
                    title={t(
                      'modals.createVehicle.steps.acceptInternationalRides.acceptInternationalRidesTooltip'
                    )}
                    placement='top'
                  >
                    <div>
                      {t('FormFields.acceptInternationalRides.label')}
                      <HelpOutlineOutlined
                        color='primary'
                        fontSize='small'
                        sx={{ verticalAlign: 'middle', ml: 0.5 }}
                      />
                    </div>
                  </StyledTooltip>
                }
                labelProps={{
                  labelPlacement: 'start',
                  sx: { mb: 2 },
                }}
                focusInitial={focusInitial}
                {...renderProps}
                field={{
                  ...renderProps.field,
                  value: acceptInternationalRides,
                }}
              />
            )}
          />
        </Grid>
      </FormControlGroup>
      <FormControlGroup error={errors?.kidFriendly}>
        <Grid container>
          <ControlledField
            control={control}
            name='kidFriendly'
            render={(renderProps) => (
              <SwitchInputField
                label={
                  <StyledTooltip
                    title={t('modals.createVehicle.steps.kidFriendly.kidFriendlyTooltip')}
                    placement='top'
                  >
                    <div>
                      {t('FormFields.kidFriendly.label')}
                      <HelpOutlineOutlined
                        color='primary'
                        fontSize='small'
                        sx={{ verticalAlign: 'middle', ml: 0.5 }}
                      />
                    </div>
                  </StyledTooltip>
                }
                labelProps={{
                  labelPlacement: 'start',
                  sx: { mb: 2 },
                }}
                focusInitial={focusInitial}
                {...renderProps}
                field={{
                  ...renderProps.field,
                  value: kidFriendly,
                }}
              />
            )}
          />
        </Grid>
      </FormControlGroup>
      <FormControlGroup error={errors?.extras as any} required>
        <Grid container>
          {extrasList.map((arr, arrIndex) => {
            return (
              <Grid item xs={12} md={6} key={arr.join(',')}>
                {arr.map((key, i) => {
                  const accumulatedIndex = arrIndex * arr.length + i;
                  return (
                    <Box key={key} sx={{ display: 'flex', width: '100%' }}>
                      <ControlledField
                        control={control}
                        name={`extras.${accumulatedIndex}`}
                        render={(renderProps) => {
                          const isChecked = extras.includes(key);
                          return (
                            <CheckboxInputField
                              {...renderProps}
                              field={{
                                ...renderProps.field,
                                value: isChecked,
                                onChange: () => {
                                  setValue(
                                    'extras',
                                    isChecked ? extras.filter((el) => el !== key) : [...extras, key]
                                  );
                                },
                              }}
                              translationKey={`vehicleExtras.${key}`}
                            />
                          );
                        }}
                      />
                    </Box>
                  );
                })}
              </Grid>
            );
          })}
        </Grid>
      </FormControlGroup>
    </>
  );
}

function VehicleExtrasForm({
  defaultValues,
  vehicle,
}: {
  defaultValues?: VehicleDtoSchema;
  vehicle: VehicleDtoSchema;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<VehicleExtrasSchemaType>({
    defaultValues: {
      availableForOffer: false,
      extras: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleExtrasSchema),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const putOperatorsVehiclesMutation = usePutOperatorsVehiclesMutation();
  const onValid = async (data: VehicleExtrasSchemaType) => {
    const payload: VehicleDtoSchema = {
      ...vehicle,
      ...data,
      extras: data.extras.filter(Boolean),
    };
    await putOperatorsVehiclesMutation.mutateAsync(payload);
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleExtrasFormFields form={formInstance} />
      <Button
        type='submit'
        variant='contained'
        disabled={isSubmitting || isValidating || isLoading}
        fullWidth
        sx={{ mt: '2rem' }}
      >
        {t('save')}
      </Button>
    </form>
  );
}

export default VehicleExtrasForm;
